import React from 'react';
import DOMPurify from 'dompurify';
import {
  capitalize, get, isEmpty, isString
} from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import ReviewOutlinedSVG from 'src/assets/svg/review-outlined.svg';
import { Base, Button } from 'src/components';
import { Flyout } from 'src/componentsTailwind/index';
import { useCompany } from 'src/queries/company';
import { useDeleteReview } from 'src/queries/reviews';
import { useTree } from 'src/queries/tree';
import appUtils from 'src/components/appUtils';
import CircledIcon from 'src/components/CircledIcon/CircledIcon';
import COMMON_CONSTANTS from 'common/commonConstants';
import commonDateUtils from 'common/commonDateUtils';
import { FEEDBACK_REQUESTED_BY } from 'common/commonFeedbackConstants';
import commonPermissions from 'common/commonPermissions';
import commonQuestionsUtils from 'common/commonQuestionsUtils';
import commonReviewUtils from 'common/commonReviewUtils';
import commonTreeUtils from 'common/commonTreeUtils';

const { ACCESS, REVIEW_STATUS, FEEDBACK_TYPE } = COMMON_CONSTANTS;

const FeedbackFlyout = (props = {}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { feedback = {}, setIsOpen = () => {} } = props;

  const loggedUser = appUtils.getLoggedUser();
  const loggedUserId = appUtils.getLoggedUserId();

  const {
    data: { tree },
    isFetching: isFetchingTree,
    isError: isErrorTree
  } = useTree();

  const {
    data: company,
    isFetching: isFetchingCompany,
    isError: isErrorCompany
  } = useCompany();

  const { delete: deleteReview, isMutating: isLoadingDeleteReview } = useDeleteReview();

  const isFetching = isFetchingTree || isFetchingCompany;
  const isError = isErrorTree || isErrorCompany;
  const isReady = tree && tree.id && company && company.id && !isFetching && !isError;

  if (!isReady) {
    return null;
  }

  const isPlaceholding = isEmpty(feedback);

  if (isPlaceholding) {
    return <Flyout {...props} />;
  }

  const isAdmin = loggedUser.access === ACCESS.ADMIN;

  const {
    reviewee,
    reviewer,
    reviewerName,
    revieweeName,
    text,
    sentiment,
    visibleText,
    roleId,
    categoryId,
    question,
    reviewedDate,
    feedbackType,
    answer,
    scheduledDate,
    status,
    requestText,
    requestedBy
  } = feedback;

  const feedbackId = feedback._id || feedback.id;
  const isCategoryFeedback = feedbackType === FEEDBACK_TYPE.CATEGORY_FEEDBACK;
  const isGeneralFeedback = !isCategoryFeedback;
  const isReviewer = reviewer === loggedUserId;
  const isReviewed = [REVIEW_STATUS.REVIEWED, REVIEW_STATUS.NOT_AVAIL].includes(
    status
  );
  const isSent = status === REVIEW_STATUS.SENT;
  const isScheduled = status === REVIEW_STATUS.SCHEDULED;

  const isUnreviewed = isSent || isScheduled;
  const isRequestedBySystem = requestedBy === FEEDBACK_REQUESTED_BY.SYSTEM;

  const editFeedback = () => {
    if (isCategoryFeedback) return navigate(`/dashboard/reviews/edit/${feedbackId}`);
    return navigate(`/dashboard/feedback/edit/${feedbackId}`);
  };

  const goToFeedbackDue = () => {
    navigate(
      `/dashboard/inbox/reviews?reviewId=${feedbackId}&redir=${pathname}`
    );
  };

  const companyQuestions = company.questions;
  const roleLabel = get(
    isCategoryFeedback
      && commonQuestionsUtils.findRole(companyQuestions, roleId),
    'label',
    '-'
  );
  const categoryLabel = get(
    isCategoryFeedback
      && commonQuestionsUtils.findCategory(companyQuestions, categoryId),
    'label',
    '-'
  );

  const questionLabel = question || '-';

  const isAboveReviewee = commonTreeUtils.isNodeDirectlyAbove(
    tree,
    reviewee,
    loggedUserId
  );
  const canManageReviewee = commonPermissions.canManageAccounts(loggedUser, [
    reviewee
  ]);

  const isAboveRequester = !isRequestedBySystem
    && commonTreeUtils.isNodeDirectlyAbove(tree, reviewee, loggedUserId);
  const canCancelRequest = !isRequestedBySystem
    && (isAdmin
      || loggedUserId === requestedBy
      || isAboveRequester
      || isAboveReviewee
      || canManageReviewee);

  const isBuilderPage = pathname.includes('/builder');
  const isReportPage = pathname.includes('/report');

  const canEditFeedback = (isAboveReviewee
      || isAdmin
      || canManageReviewee
      || reviewee === reviewer)
    && !isBuilderPage
    && !isReportPage;

  const sentimentRange = get(sentiment, 'range', null);
  const sentimentRangeLabel = isString(sentimentRange)
    ? capitalize(sentimentRange)
    : '-';
  const visibilityLabel = isString(visibleText)
    ? visibleText.replace(/\.$/, '')
    : '-';

  const sanitizedText = () => ({
    __html: DOMPurify.sanitize(text)
  });

  const formattedAnswer = commonReviewUtils.getReviewAnswerLabel(feedback, {
    useSkipQuestionLabel: true
  });

  let subtitle;
  if (isUnreviewed) {
    subtitle = 'Information related to this feedback request.';
  } else {
    subtitle = 'Information related to this feedback comment.';
  }

  let dateLabel;

  if (isReviewed) {
    dateLabel = 'Date';
  } else if (isSent) {
    dateLabel = 'Sent Date';
  } else if (isScheduled) {
    dateLabel = 'Scheduled Date';
  }

  let date;

  if (isUnreviewed) {
    date = scheduledDate;
  } else {
    date = reviewedDate || reviewedDate;
  }

  return (
    <Flyout {...props}>
      <Base classes='h-[100%] w-full' relative>
        <CircledIcon svg={<ReviewOutlinedSVG className='text-purple' />} />
        <h2 className='text-xl font-bold h-[3%] mr-8'>Feedback Details</h2>
        <p className='text-sm font-normal mt-2 mb-4 leading-5 text-bluish-gray h-[3%]'>
          {subtitle}
        </p>
        <div className='border-b border-mid-gray my-3 transform -translate-x-[32px] w-[calc(100%+64px)]' />
        <div className='flex flex-col gap-4 h-[80%]'>
          <div>
            <p className='text-black font-bold leading-5 text-md m-0'>
              Reviewer
            </p>
            <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
              {reviewerName}
            </p>
          </div>
          <div>
            <p className='text-black font-bold leading-5 text-md m-0'>
              Reviewee
            </p>
            <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
              {revieweeName}
            </p>
          </div>
          <div>
            <p className='text-black font-bold leading-5 text-md m-0'>
              {dateLabel}
            </p>
            <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
              {commonDateUtils.dateToMonthDayYearTimeFormat(new Date(date))}
            </p>
          </div>
          <div>
            <p className='text-black font-bold leading-5 text-md m-0'>
              Feedback Type
            </p>
            <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
              {`${isCategoryFeedback ? 'Category' : 'General'} Feedback`}
            </p>
          </div>
          {isReviewed ? (
            <>
              {isCategoryFeedback ? (
                <div>
                  <p className='text-black font-bold leading-5 text-md m-0'>
                    Feedback Question
                  </p>
                  <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                    {questionLabel}
                  </p>
                </div>
              ) : null}
              <div>
                <p className='text-black font-bold leading-5 text-md m-0'>
                  Feedback Comment
                </p>
                <p
                  className='ql-editor p-0 m-0 !h-fit [*>]:text-bluish-gray [*>]:font-normal [*>]:leading-5 [*>]:text-sm [*>]:m-0'
                  dangerouslySetInnerHTML={sanitizedText()}
                />
              </div>
              {isCategoryFeedback ? (
                <>
                  <div>
                    <p className='text-black font-bold leading-5 text-md m-0'>
                      Role
                    </p>
                    <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                      {roleLabel}
                    </p>
                  </div>
                  <div>
                    <p className='text-black font-bold leading-5 text-md m-0'>
                      Category
                    </p>
                    <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                      {categoryLabel}
                    </p>
                  </div>
                  {answer ? (
                    <div>
                      <p className='text-black font-bold leading-5 text-md m-0'>
                        Feedback Answer
                      </p>
                      <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                        {formattedAnswer}
                      </p>
                    </div>
                  ) : null}
                  <div>
                    <p className='text-black font-bold leading-5 text-md m-0'>
                      Sentiment
                    </p>
                    <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                      {sentimentRangeLabel}
                    </p>
                  </div>
                </>
              ) : null}
              <div>
                <p className='text-black font-bold leading-5 text-md m-0'>
                  Feedback Visibility
                </p>
                <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                  {visibilityLabel}
                </p>
              </div>
            </>
          ) : null}
          {isUnreviewed ? (
            <>
              <div>
                <p className='text-black font-bold leading-5 text-md m-0'>
                  Status
                </p>
                <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                  {status}
                </p>
              </div>
              {isCategoryFeedback && isSent ? (
                <>
                  <div>
                    <p className='text-black font-bold leading-5 text-md m-0'>
                      Feedback Question
                    </p>
                    <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                      {questionLabel}
                    </p>
                  </div>
                  <div>
                    <p className='text-black font-bold leading-5 text-md m-0'>
                      Role
                    </p>
                    <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                      {roleLabel}
                    </p>
                  </div>
                  <div>
                    <p className='text-black font-bold leading-5 text-md m-0'>
                      Category
                    </p>
                    <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                      {categoryLabel}
                    </p>
                  </div>
                </>
              ) : null}
            </>
          ) : null}
          {isGeneralFeedback && !isEmpty(requestText) ? (
            <>
              <div>
                <p className='text-black font-bold leading-5 text-md m-0'>
                  Feedback Prompt
                </p>
                <p className='text-bluish-gray font-normal leading-5 text-sm m-0'>
                  {requestText}
                </p>
              </div>
            </>
          ) : null}
        </div>
        <div className='flex w-full justify-end items-end gap-4 h-[10%]'>
          <Button
            classes='!h-11 !rounded-md !text-base !font-normal'
            variant='empty-with-border'
            onClick={() => setIsOpen(false)}
          >
            Close
          </Button>
          {canEditFeedback && isReviewed ? (
            <Button
              type='button'
              variant='custom'
              classes='bg-[#0F172A] text-white text-base px-4 py-2 rounded-md transition-colors duration-300 h-11'
              onClick={editFeedback}
            >
              Edit Feedback
            </Button>
          ) : null}
          {isReviewer && isSent ? (
            <Button
              type='button'
              variant='custom'
              classes='bg-[#0F172A] text-white text-base px-4 py-2 rounded-md transition-colors duration-300 h-11'
              onClick={goToFeedbackDue}
            >
              Complete Feedback
            </Button>
          ) : null}
          {isScheduled && canCancelRequest ? (
            <Button
              classes='!h-11 !rounded-md !text-base !font-normal !text-white !border-red !bg-red'
              variant='empty-with-border'
              onClick={async () => {
                await deleteReview(feedbackId);
                setIsOpen(false);
              }}
            >
              Cancel Request
            </Button>
          ) : null}
        </div>
      </Base>
    </Flyout>
  );
};

export default FeedbackFlyout;
